import { z } from "zod";
import { ApiError, apiRequest, getStoredToken } from "../api/client";
import { parseSupportAnswer, parseSupportFeedback, publicSupportClient, SUPPORT_REQUEST_TIMEOUT_MS } from "./api";
import type { SupportChatClient } from "./api";

const reviewerStatusSchema = z.object({
  available: z.boolean(),
  knowledge_version: z.string().nullable(),
  support_url: z.string().nullable(),
  mode: z.enum(["reviewer_preview", "disabled"]),
});

// This transport is mounted only inside the authenticated session boundary.
// Eligibility belongs to the server, never an email or role check in the browser.
export function createSessionSupportClient(token: string): SupportChatClient {
  let reviewer = false;

  function assertCurrentSession(signal: AbortSignal) {
    if (signal.aborted || getStoredToken() !== token) throw new DOMException("Session changed", "AbortError");
  }

  async function request(path: string, signal: AbortSignal, body?: unknown): Promise<unknown> {
    assertCurrentSession(signal);
    const controller = new AbortController();
    const abort = () => controller.abort();
    signal.addEventListener("abort", abort, { once: true });
    let timedOut = false;
    const timeout = window.setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, SUPPORT_REQUEST_TIMEOUT_MS);
    try {
      const result = await apiRequest<unknown>(`/api/support-chat/reviewer/${path}`, {
        method: body === undefined ? "GET" : "POST",
        headers: { Accept: "application/json" },
        credentials: "omit",
        cache: "no-store",
        signal: controller.signal,
        ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      });
      assertCurrentSession(signal);
      return result;
    } catch (error) {
      if (timedOut) throw new Error("The request took too long. Please try again.");
      assertCurrentSession(signal);
      if (error instanceof ApiError) {
        if (error.status === 429) throw new Error("Too many questions right now. Please wait a moment and try again.");
        if (error.status === 401 || error.status === 403) throw new Error("Private reviewer access is unavailable. Please check your session.");
        if (error.status === 503) throw new Error("Product help is temporarily unavailable. Please try again later.");
        throw new Error("We could not complete your request. Please try again.");
      }
      throw new Error("We could not connect to product help. Check your connection and try again.");
    } finally {
      window.clearTimeout(timeout);
      signal.removeEventListener("abort", abort);
    }
  }

  return {
    async getStatus(signal) {
      reviewer = false;
      try {
        const status = reviewerStatusSchema.parse(await request("status", signal));
        assertCurrentSession(signal);
        if (status.available && status.mode === "reviewer_preview") {
          reviewer = true;
          return status;
        }
      } catch {
        assertCurrentSession(signal);
      }
      // Non-reviewers retain the existing credential-free published help. This
      // selection happens before a conversation, never as a retry of draft text.
      return publicSupportClient.getStatus(signal);
    },
    async ask(question, signal) {
      assertCurrentSession(signal);
      if (!reviewer) return publicSupportClient.ask(question, signal);
      return parseSupportAnswer(await request("answer", signal, { question, locale: "en" }));
    },
    async feedback(requestId, rating, signal) {
      assertCurrentSession(signal);
      if (!reviewer) return publicSupportClient.feedback(requestId, rating, signal);
      parseSupportFeedback(await request("feedback", signal, { request_id: requestId, rating }));
    },
  };
}
